import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Navigation,
  Plane,
  Anchor,
  Users,
  Radar,
  Save,
  Loader2,
  Sparkles,
  AlertCircle,
  X,
  Tag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/notes/MarkdownEditor';
import { AssociationSelector } from '@/components/notes/AssociationSelector';
import { AIOptimizePanel } from '@/components/notes/AIOptimizePanel';
import { notesApi, routeApi } from '@/services/notesApi';
import { fsopApi } from '@/services/api';
import { aiApi } from '@/services/aiApi';
import { useToast } from '@/components/ui/use-toast';
import type { PhaseNoteDraft, NotePhase } from '@/types/notes.types';
import type { AINoteSuggestion } from '@/types/ai.types';
import type { FlightLog } from '@/services/api';

const TABS: { phase: NotePhase; label: string; icon: React.ReactNode; placeholder: string }[] = [
  { phase: 'takeoff', label: '起飞', icon: <Navigation className="w-4 h-4" />, placeholder: '记录起飞阶段情况...' },
  { phase: 'cruise', label: '巡航/航线', icon: <Plane className="w-4 h-4" />, placeholder: '记录巡航或航线相关情况...' },
  { phase: 'landing', label: '落地', icon: <Anchor className="w-4 h-4" />, placeholder: '记录落地阶段情况...' },
  { phase: 'crew', label: '机组', icon: <Users className="w-4 h-4" />, placeholder: '记录机组人员相关情况...' },
  { phase: 'general', label: '通用/总结', icon: <Radar className="w-4 h-4" />, placeholder: '记录通用事项或总结...' },
];

interface PhaseNoteFormProps {
  flightLogId?: number | null;
  flightLegId?: number | null;
  onSaved?: () => void;
  onCancel?: () => void;
}

function createEmptyDraft(phase: NotePhase): PhaseNoteDraft {
  return {
    phase,
    title: '',
    content: '',
    tags: [],
    routeId: null,
    departureAirportCode: null,
    arrivalAirportCode: null,
    crewIds: [],
  };
}

export function PhaseNoteForm({ flightLogId, flightLegId, onSaved, onCancel }: PhaseNoteFormProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<NotePhase>('takeoff');
  const [drafts, setDrafts] = useState<Record<NotePhase, PhaseNoteDraft>>({
    takeoff: createEmptyDraft('takeoff'),
    cruise: createEmptyDraft('cruise'),
    landing: createEmptyDraft('landing'),
    crew: createEmptyDraft('crew'),
    general: createEmptyDraft('general'),
  });
  const [flightLog, setFlightLog] = useState<FlightLog | null>(null);
  const [loadingFlightLog, setLoadingFlightLog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<AINoteSuggestion | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(false);

  // 加载 AI 配置
  useEffect(() => {
    aiApi
      .getUserAIConfig()
      .then((res) => setAiEnabled(res.data.data.isEnabled && res.data.data.hasApiKey))
      .catch(() => setAiEnabled(false));
  }, []);

  // 加载 flightLog 详情并预填
  useEffect(() => {
    if (!flightLogId) return;

    setLoadingFlightLog(true);
    fsopApi
      .getFlightLog(flightLogId)
      .then(async (res) => {
        const log = res.data.data;
        setFlightLog(log);

        let routeId: number | null = null;
        if (log.departure && log.arrival) {
          try {
            const routeRes = await routeApi.getRoutes({
              departure: log.departure,
              arrival: log.arrival,
              pageSize: 1,
            });
            routeId = routeRes.data.results?.[0]?.id ?? null;
          } catch {
            routeId = null;
          }
        }

        setDrafts((prev) => ({
          takeoff: {
            ...prev.takeoff,
            routeId,
            departureAirportCode: log.departure || null,
          },
          cruise: {
            ...prev.cruise,
            routeId,
            departureAirportCode: log.departure || null,
            arrivalAirportCode: log.arrival || null,
          },
          landing: {
            ...prev.landing,
            routeId,
            arrivalAirportCode: log.arrival || null,
          },
          crew: {
            ...prev.crew,
            routeId,
            departureAirportCode: log.departure || null,
            arrivalAirportCode: log.arrival || null,
          },
          general: {
            ...prev.general,
            routeId,
            departureAirportCode: log.departure || null,
            arrivalAirportCode: log.arrival || null,
          },
        }));
      })
      .catch(() => {
        toast({ title: '获取飞行记录详情失败', variant: 'destructive' });
      })
      .finally(() => setLoadingFlightLog(false));
  }, [flightLogId, toast]);

  const updateDraft = useCallback((phase: NotePhase, patch: Partial<PhaseNoteDraft>) => {
    setDrafts((prev) => ({
      ...prev,
      [phase]: { ...prev[phase], ...patch },
    }));
  }, []);

  const handleAddTag = (phase: NotePhase) => {
    const draft = drafts[phase];
    const input = window.prompt('输入标签');
    if (input) {
      const tag = input.trim();
      if (tag && !draft.tags.includes(tag)) {
        updateDraft(phase, { tags: [...draft.tags, tag] });
      }
    }
  };

  const handleRemoveTag = (phase: NotePhase, tag: string) => {
    updateDraft(phase, { tags: drafts[phase].tags.filter((t) => t !== tag) });
  };

  const handleRequestAnalyze = async () => {
    const draft = drafts[activeTab];
    if (!draft.content.trim() && !draft.title.trim()) {
      toast({ title: '请先输入标题或内容', variant: 'destructive' });
      return;
    }

    setAiLoading(true);
    try {
      const res = await aiApi.analyzeNote({
        title: draft.title,
        content: draft.content,
        flightLog: flightLogId,
        flightLeg: flightLegId,
      });
      setAiSuggestion(res.data.data);
      toast({ title: 'AI 分析完成' });
    } catch {
      toast({ title: 'AI 分析失败', variant: 'destructive' });
    } finally {
      setAiLoading(false);
    }
  };

  const handleApplySuggestion = (suggestion: AINoteSuggestion) => {
    const draft = drafts[activeTab];
    updateDraft(activeTab, {
      title: suggestion.suggestedTitle || draft.title,
      tags: suggestion.suggestedTags.length > 0 ? suggestion.suggestedTags : draft.tags,
      phase: suggestion.suggestedPhase,
      routeId: suggestion.suggestedRouteId ?? draft.routeId,
      departureAirportCode: suggestion.suggestedDepartureAirportCode ?? draft.departureAirportCode,
      arrivalAirportCode: suggestion.suggestedArrivalAirportCode ?? draft.arrivalAirportCode,
      crewIds: suggestion.suggestedCrewIds.length > 0 ? suggestion.suggestedCrewIds : draft.crewIds,
      aiSuggested: true,
      aiSuggestionConfidence: suggestion.confidence,
    });
    setAiSuggestion(null);
    toast({ title: '已应用 AI 建议' });
  };

  const handleSave = async () => {
    // 过滤掉空内容
    const notesToCreate = Object.values(drafts).filter(
      (d) => d.title.trim() || d.content.trim()
    );

    if (notesToCreate.length === 0) {
      toast({ title: '请至少填写一条笔记', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const res = await notesApi.batchCreateNotes({
        flightLog: flightLogId,
        flightLeg: flightLegId,
        notes: notesToCreate,
      });

      if (res.data.success) {
        toast({ title: `已创建 ${res.data.data.createdCount} 条笔记` });
        if (onSaved) {
          onSaved();
        } else {
          navigate('/notes');
        }
      } else {
        toast({ title: res.data.message || '创建失败', variant: 'destructive' });
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || '保存失败';
      toast({ title: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loadingFlightLog) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* 顶部上下文 */}
      {flightLog && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 text-sm font-medium text-blue-800">
            <Plane className="w-4 h-4" />
            <span>
              {flightLog.airNum} {flightLog.airline} ({flightLog.departure} → {flightLog.arrival})
            </span>
          </div>
          <div className="text-xs text-blue-600 mt-1">飞行日期：{flightLog.airDate}</div>
        </div>
      )}

      {/* Tab 切换 */}
      <div className="flex gap-1 overflow-x-auto pb-2 mb-4 border-b">
        {TABS.map((tab) => {
          const hasContent = drafts[tab.phase].title || drafts[tab.phase].content;
          return (
            <button
              key={tab.phase}
              onClick={() => {
                setActiveTab(tab.phase);
                setAiSuggestion(null);
              }}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2 text-sm font-medium whitespace-nowrap rounded-t-lg transition-colors',
                activeTab === tab.phase
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50',
                hasContent && activeTab !== tab.phase && 'text-green-600'
              )}
            >
              {tab.icon}
              {tab.label}
              {hasContent && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
            </button>
          );
        })}
      </div>

      {/* 当前 Tab 表单 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Input
            value={drafts[activeTab].title}
            onChange={(e) => updateDraft(activeTab, { title: e.target.value })}
            placeholder={`${TABS.find((t) => t.phase === activeTab)?.label}笔记标题...`}
            className="text-lg font-semibold"
          />
          <MarkdownEditor
            value={drafts[activeTab].content}
            onChange={(value) => updateDraft(activeTab, { content: value })}
            placeholder={TABS.find((t) => t.phase === activeTab)?.placeholder}
          />

          {/* 标签 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Tag className="w-4 h-4 inline mr-1" />
              标签
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {drafts[activeTab].tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded cursor-pointer hover:bg-gray-200"
                  onClick={() => handleRemoveTag(activeTab, t)}
                >
                  {t}
                  <span className="text-gray-400">×</span>
                </span>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => handleAddTag(activeTab)}>
              + 添加标签
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          <AssociationSelector
            phase={activeTab}
            selectedRouteId={drafts[activeTab].routeId}
            onRouteChange={(routeId) => updateDraft(activeTab, { routeId })}
            selectedDepartureAirportCode={drafts[activeTab].departureAirportCode}
            onDepartureAirportChange={(code) => updateDraft(activeTab, { departureAirportCode: code })}
            selectedArrivalAirportCode={drafts[activeTab].arrivalAirportCode}
            onArrivalAirportChange={(code) => updateDraft(activeTab, { arrivalAirportCode: code })}
            selectedCrewIds={drafts[activeTab].crewIds}
            onCrewChange={(ids) => updateDraft(activeTab, { crewIds: ids })}
          />

          {aiEnabled && (
            <AIOptimizePanel
              suggestion={aiSuggestion}
              loading={aiLoading}
              onApply={handleApplySuggestion}
              onDiscard={() => setAiSuggestion(null)}
              onRequestAnalyze={handleRequestAnalyze}
            />
          )}

          {!aiEnabled && (
            <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-500 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>在设置中配置 AI API Key 后，可使用 AI 自动优化标题、标签和关联维度。</span>
            </div>
          )}
        </div>
      </div>

      {/* 底部固定操作栏 */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t px-4 py-3 flex items-center justify-between z-40">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Sparkles className="w-4 h-4" />
          <span>已填写 {Object.values(drafts).filter((d) => d.title || d.content).length} / {TABS.length} 个维度</span>
        </div>
        <div className="flex gap-2">
          {onCancel && (
            <Button variant="outline" onClick={onCancel} disabled={saving}>
              <X className="w-4 h-4 mr-1" />
              取消
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            保存全部笔记
          </Button>
        </div>
      </div>

      {/* 底部占位，避免内容被固定栏遮挡 */}
      <div className="h-20" />
    </div>
  );
}

function cn(...classes: (string | false | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}
